import React from "react";
import { Crosshair, Shield, Scissors, Layers, Cpu, Eye } from "lucide-react";

const ITEMS = [
  {
    icon: Crosshair,
    title: "Centering",
    desc: "Sub-pixel border measurement across all four edges, front and back. No ruler can match our optical alignment.",
    span: "md:col-span-7",
    testid: "feature-centering",
  },
  {
    icon: Shield,
    title: "Corners",
    desc: "Microscopic wear detection. 40MP macro capture identifies the tiniest ding or softening.",
    span: "md:col-span-5",
    testid: "feature-corners",
  },
  {
    icon: Scissors,
    title: "Edges",
    desc: "Multi-angle laser profiling reveals whitening, chipping, and fray invisible to the naked eye.",
    span: "md:col-span-5",
    testid: "feature-edges",
  },
  {
    icon: Layers,
    title: "Surface",
    desc: "Holographic topography and print-line mapping across every millimeter of the card face.",
    span: "md:col-span-7",
    testid: "feature-surface",
  },
];

const META = [
  { icon: Cpu, label: "Vision Model", value: "BGS-Net v4" },
  { icon: Eye, label: "Scan Resolution", value: "40 MP × 6 angles" },
  { icon: Shield, label: "Consistency", value: "99.98%" },
];

export const HowItWorks = () => {
  return (
    <section id="how" className="relative py-24 md:py-32" data-testid="how-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="chip mb-6">The Process</div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
            Four subgrades.<br />
            <span className="text-brand">One algorithm.</span>
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-2xl">
            Every card passes through a multi-stage computer vision pipeline trained on millions of reference grades.
            The result: a final grade that never drifts with fatigue, bias, or opinion.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5">
          {ITEMS.map(({ icon: Icon, title, desc, span, testid }) => (
            <div
              key={title}
              data-testid={testid}
              className={`group relative overflow-hidden col-span-1 ${span} glass rounded-2xl p-8 md:p-10 transition-all duration-500 hover:-translate-y-1 hover:border-brand/40`}
            >
              <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-brand/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="flex items-start justify-between">
                <Icon size={28} className="text-brand" />
                <span className="font-mono text-xs text-white/30">0{ITEMS.indexOf(ITEMS.find(i=>i.title===title))+1}</span>
              </div>
              <h3 className="mt-8 font-display text-2xl md:text-3xl font-bold tracking-tight">{title}</h3>
              <p className="mt-3 text-white/60 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
          {META.map(({ icon: Icon, label, value }) => (
            <div key={label} className="glass rounded-xl px-6 py-5 flex items-center gap-4">
              <Icon size={22} className="text-brand" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-semibold">{label}</div>
                <div className="font-display font-bold text-white text-lg">{value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
